import { type ComputedRef, computed } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { useDrainPages } from '@/composables/useDrainPages'
import { fetchPerformers, type Performer } from '@/api/performers'
import { performersPollingInterval } from '@/config/api'

/**
 * Drains the full `['performers']` list — performers are a small, bounded
 * catalog (teachers/staff), so resolving a group's `performer_ids` by name
 * needs every one of them, not a capped search-driven subset like the
 * booking person picker's `PERFORMER_OPTIONS_CAP`. Shares the query key
 * with any other full-list consumer.
 *
 * @param options.enabled - `GET /persons/performers` requires `StuffView`
 * (see `person_endpoints.py`'s `PERSON_GET_PERFORMERS` route), which a
 * caller's viewer isn't guaranteed to hold — e.g. the Groups pane, reached
 * via `ClientsView`/`GroupsView` alone. Pass a reactive `hasRole(...)`
 * check so a 403 doesn't trip the shared axios interceptor's app-wide
 * navigation; defaults to always-enabled.
 */
export function useAllPerformers(options?: { enabled?: ComputedRef<boolean> | boolean }) {
  const query = useInfiniteQuery({
    queryKey: ['performers'],
    queryFn: ({ pageParam }) => fetchPerformers(pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    enabled: options?.enabled ?? true,
    staleTime: performersPollingInterval,
    refetchInterval: performersPollingInterval,
    refetchIntervalInBackground: false,
  })

  useDrainPages(query)

  const performers = computed<Performer[]>(() =>
    (query.data.value?.pages ?? []).flatMap((page) => page.items ?? []),
  )

  const isDraining = computed(() => query.isPending.value || query.hasNextPage.value === true)

  return {
    performers,
    isPending: query.isPending,
    isError: query.isError,
    isDraining,
  }
}
